<template>
  <div>
    <VDataTable
      :headers="headers"
      :items="items"
      :pagination.sync="pagination"
      hide-actions
      :loading="loading"
      :expand="expandAll"
      :item-key="itemKey"
    >
      <template v-slot:progress>
        <VProgressLinear color="#333" indeterminate></VProgressLinear>
      </template>
      <template v-slot:items="slotItemsProps">
        <!-- @slot Pass in a custom table row to override the default row. -->
        <slot name="item" :item-props="slotItemsProps">
          <tr @click="handleRowClick(slotItemsProps)">
            <td
              v-for="(i, itemIndex) in Object.keys(slotItemsProps.item)"
              :key="itemIndex"
              :class="{
                'action-container':
                  itemIndex === Object.keys(slotItemsProps.item).length - 1 &&
                  $slots.actions,
              }"
            >
              {{ slotItemsProps.item[i] }}
              <span
                v-if="
                  itemIndex === Object.keys(slotItemsProps.item).length - 1 &&
                  $slots.actions
                "
                class="actions"
              >
                <!-- @slot The 'actions' slot is to allow icons or buttons to the right of the last column of the table. use @click.stop="..." to stop propagation -->
                <slot name="actions" />
              </span>
            </td>
          </tr>
        </slot>
      </template>

      <template v-slot:expand="{ index, item, expanded }">
        <!-- @slot The 'expandedContent' slot is to have a accoridon effect per row. -->
        <slot name="expandedContent" :content="{ index, item, expanded }" />
      </template>
    </VDataTable>
    <div class="text-md-right text-xs-center">
      <VPagination v-model="pagination.page" :length="pages" />
    </div>
  </div>
</template>

<script>
import { VDataTable, VPagination, VProgressLinear } from 'vuetify/lib';
/**
 * Original Author: Josh Porter
 * Original Team: Unidrythons
 *
 * Gitlab file https://code.tls.nuskin.io/ns-am/ux/design-components/-/blob/master/components/ns-table/NsTable.vue
 */
export default {
  name: 'NsTable',
  components: {
    VDataTable,
    VPagination,
    VProgressLinear,
  },
  props: {
    /**
     * An array of objects that each describe a header column. See the example below for a definition of all properties.
     */
    headers: {
      type: Array,
      default: () => {
        return [];
      },
    },
    /**
     * The array of table rows
     */
    items: {
      type: Array,
      default: () => {
        return [];
      },
    },
    /**
     * the amount of table rows you want to display at one time. default is 5.
     */
    rows: {
      type: Number,
      default: 5,
    },
    /**
     * Displays linear progress bar. accepts hex values as a string.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Keeps all expanded content displayed at the same time
     */
    expandAll: {
      type: Boolean,
      default: false,
    },
    /**
     * the field in the item object that designates and unique key.
     */
    itemKey: {
      type: String,
      default: 'name',
    },
  },
  data() {
    return {
      pagination: {
        rowsPerPage: this.rows,
        totalItems: this.items.length,
      },
      expand: this.expandAll,
    };
  },
  computed: {
    pages() {
      if (
        this.pagination.rowsPerPage == null ||
        this.pagination.totalItems == null
      ) {
        return 0;
      }

      return Math.ceil(
        this.pagination.totalItems / this.pagination.rowsPerPage
      );
    },
  },
  mounted() {
    this.pagination.rowsPerPage = this.rows;
  },
  methods: {
    handleRowClick(slotItemsProps) {
      slotItemsProps.expanded = !slotItemsProps.expanded;
      /**
       * Emits an event when row is clicked with params: function (item){}
       */
      this.$emit('row', slotItemsProps.item);
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use '~@nuskin/ns-core-styles/src/scss/colors';
@use '~@nuskin/ns-core-styles/src/scss/buttons';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
.v-pagination__item {
  @extend %button-base;
  &.primary {
    background: map.get(colors.$baseColors, 'blackhole') !important;
    border-color: map.get(colors.$baseColors, 'blackhole') !important;
    @extend .primary-solid;
  }
}
.v-progress-linear__background {
  &.primary {
    background: map.get(colors.$baseColors, 'shells') !important;
    border-color: map.get(colors.$baseColors, 'shells') !important;
    padding: 0;
  }
}
.v-progress-linear__bar__indeterminate .long,
.v-progress-linear__bar__indeterminate .short {
  top: -2px;
  &.primary {
    padding: 0;
    background: map.get(colors.$baseColors, 'shells') !important;
    border-color: map.get(colors.$baseColors, 'shells') !important;
  }
}
.v-table {
  font-family: map.get(
    typographyVariables.$typography,
    'font-family-primary'
  ) !important;
  th {
    font-size: 14px;
    text-transform: uppercase;
    font-weight: 600 !important;
  }
  .action-container {
    display: flex;
    align-items: center;
    flex-flow: row nowrap;
    .actions {
      margin-left: auto;
      display: block;
    }
  }
}
.v-table tbody tr:hover:not(.v-datatable__expand-row) {
  background: map.get(colors.$baseColors, 'ariel') !important;
}
.v-pagination {
  &__navigation {
    box-shadow: none;
    border: 1px solid map.get(colors.$baseColors, 'dryink') !important;
  }
  &__item {
    box-shadow: none;
    &--active {
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2),
        0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
    }
  }
}
</style>
